import FieldType from "@/utils/constants/FieldType.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {ValueFormatterParams} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import ColumnFiltrationFactory, {FilterData} from "@/utils/UIConstants/ColumnFiltrationFactory.ts";
import AGGridDateTimeFilter from "@/components/inputs/AGGridDateTimeFilter.vue";
import {NamaAGFilter} from "@/utils/presenters/interfaces.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";

export default class ColumnFiltrationServerSideFactory
{
  static fetchFilterData(column: namaTypes.ListScreenColumn, entityType: string, layout?: namaTypes.ListScreenLayout & namaTypes.SearcherScreenLayout): FilterData | null | undefined {
    let type = column.metaData?.fieldType;
    if (column.arabicTemplate || column.englishTemplate || column.sources)
      return;
    if (!type)
      return;
    switch (type) {
      case FieldType.Text:
      case FieldType.Link:
      case FieldType.Email:
      case FieldType.BigText:
      case FieldType.PhoneNumber:
      case FieldType.FieldID:
      case FieldType.Measures:
      case FieldType.Color:
      case FieldType.LongToken:
      case FieldType.LatLng:
      case FieldType.ID:
        return new FilterData("agTextColumnFilter" as NamaAGFilter, {
          textFormatter: (value: string): string | null => value == null ? null : StringUtils.replaceArNumerals(value),
          textMatcher: ColumnFiltrationFactory.textMatcher,
          maxNumConditions: 1,
          buttons: ["reset"]
        });
      case FieldType.Decimal:
      case FieldType.Long:
      case FieldType.Integer:
        return new FilterData("agNumberColumnFilter" as NamaAGFilter, {
          inRangeInclusive: true,
          maxNumConditions: 1,
          buttons: ["reset"]
        });
      case FieldType.Boolean:
        return new FilterData("agSetColumnFilter" as NamaAGFilter,
          {
            buttons: ["reset"],
            applyMiniFilterWhileTyping: true,
            values: ['true', 'false'],
            valueFormatter: (params: ValueFormatterParams) => Translator.translateId(params.value),
          });
      case FieldType.Date:
        return new FilterData("agDateColumnFilter" as NamaAGFilter, {
            minValidYear: 1800,
            maxValidYear: 2999,
            buttons: ["reset"],
            maxNumConditions: 1,
          }
        );
      case FieldType.DateAndTime://TODO:: Need to handle equality with seconds
        // TODO :: case FieldType.Time: :: Need to handle equality with seconds and all operators not working
        return new FilterData(AGGridDateTimeFilter as NamaAGFilter, {
          fieldType: type
        });
      case FieldType.FeatureID:
      case FieldType.EntityType:
      case FieldType.Enum:
        return new FilterData("agSetColumnFilter" as NamaAGFilter, {
          buttons: ["reset"],
          applyMiniFilterWhileTyping: true,
          valueFormatter: (params: ValueFormatterParams) => ColumnFiltrationFactory.orBlank(params.value, ColumnFiltrationFactory.enumFilterSearchText(params.value, column.metaData?.enumTypeName)),
          cellRenderer: (params: ValueFormatterParams) => ColumnFiltrationFactory.orBlank(params.value, Translator.translate(params.value, column.metaData?.enumTypeName)),
          values: async (params) => {
            const values = await AGGridUtils.fetchDistinctColumnValues({
              colId: params.colDef.colId,
              entityType: entityType,
              fieldType: column.metaData?.fieldType,
              referenceTo: column.metaData?.referencedEntityType,
              queriedClass: layout?.queriedClass,
            });
            params.success(values as string[]);
          }
        });
      case FieldType.Reference:
      case FieldType.Genericreference:
        return new FilterData("agMultiColumnFilter" as NamaAGFilter, {
          filters: [
            {
              filter: ColumnFiltrationFactory.fetchAGReferenceFilter(),
              filterParams: {
                metaData: column.metaData,
              }
            },
            {
              filter: ColumnFiltrationFactory.fetchAGQuestionListFilter(),
              filterParams: {
                metaData: column.metaData,
                entityType: entityType,
                queriedClass: layout?.queriedClass,
              }
            }
          ]
        });
      case FieldType.Binary:
      default:
        return;
    }
  }
}
